import React, { useReducer } from "react";

export const BoardContext = React.createContext();

const defaultSort = {
  columnID: false,
  columnType: false,
  direction: false,
};

export const boardInitialState = {
  boardID: 0,
  boardTitle: "",
  boardType: null,
  boardURL: null,
  groupID: 0,
  groupURL: null,
  everyoneGroupID: null,
  rows: [],
  rowsUnfiltered: [],
  columns: [],
  rowTotals: {},
  filters: {},
  filtersPanelOpen: false,
  filtersApplied: 0,
  sortInfo: defaultSort,
  showLoadingOverlay: false,
  filteredTotal: 0,
  unfilteredTotal: null,
  isShared: false,
  isArchived: false,
  extendedFields: [],
  savedBoardFilter: null,
  shouldUseSavedBoardFilter: true,
  groups: [],
  createdBy: null,
};

const boardReducer = (state, action) => {
  const newState = { ...state };

  switch (action.type) {
    case "setBoardType": {
      return {
        ...state,
        boardType: action.payload,
      };
    }

    case "setBoardData": {
      // handles setting all the data returned when fetching the entire board.
      const {
        boardInfo,
        rows,
        columns,
        groupID,
        everyoneGroupID,
        rowTotals,
        isShared,
        isArchived,
        savedBoardFilter,
        filteredTotal,
        unfilteredTotal,
        groups,
        createdBy,
      } = action.payload;

      const rowsUnfiltered = () => {
        if (filteredTotal === unfilteredTotal) return [];

        return [unfilteredTotal];
      };

      return {
        ...state,
        boardTitle: boardInfo.title,
        boardID: boardInfo.ID,
        groupID: groupID || 0, // fallback to 0 for non-group boards
        everyoneGroupID: everyoneGroupID,
        rows,
        columns,
        rowTotals,
        isShared,
        isArchived,
        savedBoardFilter: savedBoardFilter || state.savedBoardFilter, // don't overwrite savedBoardFilter if not provided
        filteredTotal,
        unfilteredTotal,
        rowsUnfiltered: rowsUnfiltered(),
        groups,
        createdBy,
      };
    }

    case "clearBoardData": {
      const cleanState = { ...boardInitialState };

      // these values clear/update themselves independently, so not clearing them here.
      cleanState.boardURL = state.boardURL;
      cleanState.groupURL = state.groupURL;

      return cleanState;
    }

    case "Update URLs": {
      return {
        ...state,
        groupURL: action.groupURL,
        boardURL: action.boardURL,
      };
    }

    case "Update Rows": {
      return {
        ...state,
        rows: action.payload,
      };
    }

    case "Update Rows From Filters": {
      if (newState.rowsUnfiltered.length === 0)
        newState.rowsUnfiltered = [...state.rows];
      newState.rows = [...action.payload];
      newState.filtersApplied = state.filtersApplied + 1;

      return newState;
    }

    case "Filters Cleared": {
      newState.rowsUnfiltered = [];
      newState.filtersApplied = state.filtersApplied + 1;

      return newState;
    }

    case "Update Columns": {
      return {
        ...state,
        columns: action.payload,
      };
    }

    case "Update rowTotals": {
      return {
        ...state,
        rowTotals: action.payload ? action.payload : {},
      };
    }

    case "Update filteredTotal": {
      return {
        ...state,
        filteredTotal: action.payload ? action.payload : 0,
      };
    }

    case "Update unfilteredTotal": {
      // currently the backend is not including unfilteredTotal if the current filters hide all rows.
      // So we only want to set this if value is not undefinted.

      if (action.payload !== undefined) {
        newState.unfilteredTotal = action.payload;
      }
      return newState;
    }

    case "Set Cell Value": {
      const { entityID, columnID, value } = action.payload;
      const col = newState.columns.find((col) => col.ID === columnID);
      const row = newState.rows.find((row) => row.entityID === entityID);
      if (!(col && col.accessor && row)) return newState;

      row[col.accessor] = value;

      // Optimistic update related entities if the entityFieldResponseID is in the value.
      // If we went ahead and had entityFieldResponseID in all of the data, it would be auto-awesome!
      try {
        const { entityFieldResponseID } = JSON.parse(value || "{}");
        if (entityFieldResponseID) {
          newState.rows
            .filter((maybeRelatedRow) => {
              const rowValue = maybeRelatedRow[col.accessor];
              try {
                const { entityFieldResponseID: relatedIfSame } = JSON.parse(
                  rowValue || "{}"
                );
                return relatedIfSame === entityFieldResponseID;
              } catch (e) {
                console.error(`${rowValue} is not valid JSON`);
              }
              return false;
            })
            .forEach((relatedCol) => {
              relatedCol[col.accessor] = value;
            });
        }
      } catch (e) {
        console.error(`${value} is not valid JSON`);
      }

      return newState;
    }

    case "Set Column Total": {
      const { entityID, total } = action.payload;
      const row = newState.rows.find((row) => row.entityID === entityID);
      if (!row) return newState;

      row.total = total;
      return newState;
    }

    case "Filter Panel Open": {
      return { ...state, filtersPanelOpen: true };
    }

    case "Filter Panel Closed": {
      return { ...state, filtersPanelOpen: false };
    }

    case "Set Filters": {
      return {
        ...state,
        filters: action.payload,
      };
    }

    case "Set Sort": {
      return { ...state, sortInfo: action.payload };
    }
    case "Clear Sort": {
      return { ...state, sortInfo: defaultSort };
    }

    case "Show Loading Overlay": {
      return { ...state, showLoadingOverlay: true };
    }

    case "Hide Loading Overlay": {
      return { ...state, showLoadingOverlay: false };
    }

    case "Set isShared": {
      return { ...state, isShared: action.payload };
    }

    case "Set extendedFields": {
      return { ...state, extendedFields: action.payload };
    }

    case "Set shouldUseSavedBoardFilter": {
      return { ...state, shouldUseSavedBoardFilter: action.payload };
    }

    case "Update savedBoardFilter": {
      return { ...state, savedBoardFilter: action.payload };
    }

    case "Set boardTitle": {
      return { ...state, boardTitle: action.payload };
    }

    default:
      return state;
  }
};

export function BoardContextProvider({
  children,
  initialState = boardInitialState,
}) {
  return (
    <BoardContext.Provider value={useReducer(boardReducer, initialState)}>
      {children}
    </BoardContext.Provider>
  );
}

export function useBoards() {
  const context = React.useContext(BoardContext);

  if (!context) {
    throw new Error("useBoards must be used inside a <BoardContextProvider />");
  }

  return context;
}
